import React, { useEffect, useState } from 'react';
import { CL_TOKENS } from '../../receptionist-call-logs/config';
import { Icon } from '../Icon/Icon';
import { parseDurationToSec, secToTime } from './CallLogsShared';

export interface AudioPlayerProps {
  duration: string;
  hasRecording?: boolean | undefined;
}

const SPEEDS = [1, 1.25, 1.5, 2];

export function AudioPlayer({ duration, hasRecording = true }: AudioPlayerProps) {
  const totalSec = parseDurationToSec(duration);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSec, setCurrentSec] = useState(0);
  const [speed, setSpeed] = useState(1);

  useEffect(() => {
    if (!isPlaying || !hasRecording) return;
    const id = setInterval(() => {
      setCurrentSec((s) => {
        const next = s + speed;
        if (next >= totalSec) {
          setIsPlaying(false);
          return totalSec;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isPlaying, totalSec, speed, hasRecording]);

  if (!hasRecording) {
    return (
      <div className="flex items-center gap-3 rounded-[11px] border border-[var(--cream-2)] px-3.5 py-3" style={{ background: CL_TOKENS.surfaceSubtle }}>
        <div
          className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full"
          style={{ background: CL_TOKENS.surfaceMute, color: CL_TOKENS.ink4 }}
        >
          {/* No exact 'mic-off' kind — using 'shield-off' as the nearest "disabled audio" glyph. */}
          <Icon kind="shield-off" size={14} stroke={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold tracking-[-0.005em]" style={{ color: CL_TOKENS.ink2 }}>
            No audio recording
          </div>
          <div className="mt-0.5 text-[11px]" style={{ color: CL_TOKENS.ink3 }}>
            Voicemail or no-answer call. Transcript and metadata are available below.
          </div>
        </div>
      </div>
    );
  }

  const pct = totalSec > 0 ? (currentSec / totalSec) * 100 : 0;

  return (
    <div className="rounded-[11px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-3 shadow-[var(--sh-1)]">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setIsPlaying((p) => !p)}
          className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full border-0 shadow-[var(--sh-1)]"
          style={{ background: CL_TOKENS.green, color: '#fff', cursor: 'pointer' }}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          <Icon kind={isPlaying ? 'pause' : 'play'} size={isPlaying ? 13 : 11} />
        </button>

        <div className="min-w-0 flex-1">
          <div className="relative">
            <input
              type="range"
              className="cl-scrubber"
              min={0}
              max={totalSec}
              value={currentSec}
              onChange={(e) => setCurrentSec(parseInt(e.target.value, 10))}
              aria-label="Seek"
            />
            <div
              className="pointer-events-none absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full"
              style={{ width: `${pct}%`, maxWidth: 'calc(100% - 12px)', background: CL_TOKENS.green }}
            />
          </div>
          <div className="mt-1 flex justify-between">
            <span className="cl-mono text-[11px] font-medium" style={{ color: CL_TOKENS.ink3 }}>
              {secToTime(currentSec)}
            </span>
            <span className="cl-mono text-[11px] font-medium" style={{ color: CL_TOKENS.ink4 }}>
              {duration}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={() =>
            setSpeed((s) => {
              const i = SPEEDS.indexOf(s);
              return SPEEDS[(i + 1) % SPEEDS.length] ?? 1;
            })
          }
          className="cl-mono inline-flex h-7 flex-none items-center rounded-[7px] border-0 px-[9px] text-[11px] font-semibold tracking-[-0.01em]"
          style={{ background: CL_TOKENS.surfaceMute, color: CL_TOKENS.ink2, cursor: 'pointer' }}
          title="Playback speed"
        >
          {speed}×
        </button>
      </div>
    </div>
  );
}
